/**
 * Bank-statement classification helpers (non-blocking Gemini pre-check).
 *
 * Backend stores the result on the bank_statement Tenant_doc:
 *   meta_data = JSON.stringify({ statementClassification: {
 *     isBankStatement, documentType, confidence, reason,
 *     classificationAvailable, checkedAt } })
 * and returns it as `classification` in the upload response.
 *
 * Frontend rule:
 * - classification available + isBankStatement === false
 *   → show "not a bank statement" error
 * - classification unavailable (unknown / missing)
 *   → fall back to the Algoan error (financial_info_errors)
 */

export const NOT_BANK_STATEMENT_ERROR_KEY =
  "documentNotClassifiedAsBankStatement";

const safeParseMeta = (meta) => {
  if (!meta) return null;
  if (typeof meta === "object") return meta;
  if (typeof meta !== "string") return null;
  try {
    return JSON.parse(meta);
  } catch (_) {
    return null;
  }
};

export const parseStatementClassification = (doc) => {
  const meta = safeParseMeta(doc?.meta_data ?? doc?.metaData);
  const raw =
    meta?.statementClassification ??
    meta?.classification ??
    (doc?.classification && typeof doc.classification === "object"
      ? doc.classification
      : null);
  if (!raw) return null;
  const documentType = raw.documentType || raw.actual_type || "unknown";
  const classificationAvailable =
    raw.classificationAvailable !== undefined
      ? raw.classificationAvailable === true
      : documentType !== "unknown" &&
        !String(raw.reason || "").startsWith("classification_failed");
  return {
    isBankStatement: raw.isBankStatement === true,
    documentType,
    confidence: Number(raw.confidence ?? 0),
    reason: raw.reason || "",
    classificationAvailable,
  };
};

/** Latest bank_statement doc carrying a classification, or null. */
export const getLatestBankStatementClassification = (docs = []) => {
  if (!Array.isArray(docs)) return null;
  const bankDocs = docs.filter((d) => d?.type === "bank_statement");
  // docs are usually newest-first; still prefer explicit created_at ordering
  const sorted = [...bankDocs].sort(
    (a, b) => new Date(b?.created_at) - new Date(a?.created_at)
  );
  for (const doc of sorted) {
    const parsed = parseStatementClassification(doc);
    if (parsed) return { ...parsed, fileName: doc?.file_name ?? null };
  }
  return null;
};

/**
 * Resolve which error the ConnectBankCard should show.
 * Returns { key, fallback, classification } where key is the i18n key.
 */
export const resolveFinancialDisplayError = ({
  classification,
  financialInfoErrors,
  userFinancialInfoErrors,
}) => {
  const algoanKey = financialInfoErrors || userFinancialInfoErrors || null;
  if (
    classification &&
    classification.classificationAvailable === true &&
    classification.isBankStatement === false
  ) {
    return {
      key: NOT_BANK_STATEMENT_ERROR_KEY,
      fallback: `The uploaded file was detected as “${classification.documentType}” instead of a bank statement. Please upload a valid bank statement.`,
      classification,
      isClassificationError: true,
    };
  }
  if (algoanKey) {
    return {
      key: algoanKey,
      fallback: "Tink and Algoan Error",
      classification: classification || null,
      isClassificationError: false,
    };
  }
  return {
    key: null,
    fallback: null,
    classification: classification || null,
    isClassificationError: false,
  };
};
